import { Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import AddRecordButton from '@components/AddRecordButton';
import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { ProcessStepper } from '@components/ProcessStepper/index';
import RecordCard from '@components/RecordCard';
import ViewTab from '@components/ViewTab';

import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';

import {
  NoRecordsWrapper,
  Spacer,
  WrapperDiv,
} from '@layouts/Layouts/BigTitleLayout';

import type { MedicationStatementRecord } from '@models/medication';

const messages = defineMessages({
  noRecordsFound: {
    id: 'medicationsMain.noRecordsFound',
    defaultMessage: 'No Known {type} Medications',
  },
  inactive: {
    id: 'medicationsMain.inactive',
    defaultMessage: 'Inactive',
  },
  active: {
    id: 'medicationsMain.active',
    defaultMessage: 'Active',
  },
  unknown: {
    id: 'medicationsMain.unknown',
    defaultMessage: 'Unknown',
  },
  addRecord: {
    id: 'GLOBAL.addRecord',
    defaultMessage: 'Add a Record',
  },
});

interface MedicationListProps {
  medications: MedicationStatementRecord[];
  loading: boolean;
}

const MedicationList: React.FC<MedicationListProps> = ({
  medications,
  loading,
}) => {
  const { formatMessage } = useIntl();
  const {
    state: { isPatient },
  } = useSession();

  const { palette } = useTheme();

  const tabs = [
    { id: 'active', value: formatMessage(messages.active) },
    { id: 'inactive', value: formatMessage(messages.inactive) },
    { id: 'unknown', value: formatMessage(messages.unknown) },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const tabData = medications
    .filter((med) => {
      return activeTab.id === med.getStatusCategory();
    })
    .sort((a, b) => {
      const medicationA = a.acquisitionType;
      const medicationB = b.acquisitionType;
      if (!medicationA) {
        return 1;
      }
      if (!medicationB) {
        return -1;
      }
      return medicationA === 'prescription' ? -1 : 1;
    });

  return (
    <ContentLoader loading={loading}>
      <Container maxWidth="sm">
        <WrapperDiv>
          <ViewTab
            changeTab={setActiveTab}
            selected={activeTab}
            tabArray={tabs}
          />
          <Spacer />
          <ProcessStepper
            allBlack
            stepNames={tabData.map((m) => (
              <RecordCard
                bottomText={m.dosageSummary}
                color={
                  m.acquisitionType === 'prescription'
                    ? palette.purple.main
                    : palette.teal.main
                }
                coloredSubtext
                key={m.id}
                noteDisplay={Boolean(m.recordNotes.length)}
                onClick={() => {
                  navigate(`${ROUTES.MEDICATIONS}${m.id}`);
                }}
                recordSource={m.recordSource}
                status={m.curationStatus}
                text={m.medication}
                topText={m.acquisitionTypeName}
                viewed={m.viewedStatus}
              />
            ))}
          />

          {tabData.length === 0 && (
            <NoRecordsWrapper>
              {formatMessage(messages.noRecordsFound, {
                type: activeTab.value,
              })}
            </NoRecordsWrapper>
          )}

          {isPatient ? (
            <AddRecordButton link={ROUTES.ADD_MEDICATION}>
              {formatMessage(messages.addRecord)}
            </AddRecordButton>
          ) : null}
        </WrapperDiv>
      </Container>
    </ContentLoader>
  );
};

export default withErrorBoundary(MedicationList, {
  FallbackComponent: ErrorBoundaryFallback,
});
